/**
 * Base API Client (Axios)
 * Centralized configuration for all API calls with interceptors & typed errors
 */

import axios, {
	type AxiosError,
	type AxiosInstance,
	type AxiosRequestConfig,
	type AxiosRequestHeaders,
} from 'axios';

import { ENV } from '@ekhodealer/ekho-common';
import { setFunctionHost } from '@ekhodealer/ekho-common/server';

export interface ApiResponse<T = unknown> {
	data: T;
	success: boolean;
	message?: string;
	errors?: string[];
}

export interface ApiError {
	message: string;
	status?: number;
	code?: string;
	details?: unknown;
}

type RequestOptions = {
	headers?: Record<string, string>;
	params?: Record<string, unknown>;
	signal?: AbortSignal;
	timeout?: number;
};

export class ApiClient {
	private axios: AxiosInstance;
	private authToken?: string;
	private tokenRefreshCallback?: () => Promise<string | null>;
	private isRefreshing = false;
	private failedQueue: Array<{
		resolve: (token: string) => void;
		reject: (error: unknown) => void;
	}> = [];

	constructor(baseURL?: string, config?: AxiosRequestConfig) {
		const resolvedBaseURL =
			baseURL ?? this.setAdminPortalFunctionHost(import.meta.env.VITE_CURR_ENV);

		this.axios = axios.create({
			baseURL: resolvedBaseURL,
			timeout: 15_000,
			headers: {
				'Content-Type': 'application/json',
			},
			...config,
		});

		// Attach Authorization header if token present
		this.axios.interceptors.request.use((cfg) => {
			if (this.authToken) {
				cfg.headers = {
					...cfg.headers,
					Authorization: `Bearer ${this.authToken}`,
				} as unknown as AxiosRequestHeaders;
			}
			return cfg;
		});

		// Handle responses and automatic token refresh
		this.axios.interceptors.response.use(
			(res) => res,
			async (err) => {
				const originalRequest = err.config;

				// Check if this is an authentication error and we haven't already tried to refresh
				if (
					(err.response?.status === 401 || err.response?.status === 403) &&
					!originalRequest._retry &&
					this.tokenRefreshCallback
				) {
					if (this.isRefreshing) {
						// If we're already refreshing, queue this request
						return new Promise((resolve, reject) => {
							this.failedQueue.push({ resolve, reject });
						})
							.then((token) => {
								originalRequest.headers.Authorization = `Bearer ${token}`;
								return this.axios(originalRequest);
							})
							.catch((error) => {
								return Promise.reject(this.normalizeError(error));
							});
					}

					originalRequest._retry = true;
					this.isRefreshing = true;

					try {
						// Attempt to refresh the token
						const newToken = await this.tokenRefreshCallback();

						if (newToken) {
							// Update the token and retry the original request
							this.setAuthToken(newToken);
							originalRequest.headers.Authorization = `Bearer ${newToken}`;

							// Process any queued requests
							this.processQueue(null, newToken);

							// Retry the original request
							return this.axios(originalRequest);
						} else {
							// Token refresh failed, process queue with error
							this.processQueue(new Error('Token refresh failed'), null);
							return Promise.reject(this.normalizeError(err));
						}
					} catch (refreshError) {
						// Token refresh failed, process queue with error
						this.processQueue(refreshError, null);
						return Promise.reject(this.normalizeError(err));
					} finally {
						this.isRefreshing = false;
					}
				}

				return Promise.reject(this.normalizeError(err));
			},
		);
	}

	/**
	 * Admin Portal specific function host configuration
	 * Overrides default setFunctionHost behavior for development environment
	 */
	private setAdminPortalFunctionHost(env: string): string {
		if (env === ENV.DEVELOPMENT) {
			return 'https://us-central1-airdealer-67669.cloudfunctions.net';
		}
		if (env === ENV.LOCAL) {
			return 'http://localhost:5000/airdealer-67669/us-central1';
		}
		return setFunctionHost(env);
	}

	/**
	 * Process the queue of failed requests after token refresh
	 */
	private processQueue(error: unknown, token: string | null) {
		this.failedQueue.forEach(({ resolve, reject }) => {
			if (error) {
				reject(error);
			} else if (token) {
				resolve(token);
			} else {
				reject(new Error('No token available'));
			}
		});

		this.failedQueue = [];
	}

	private buildConfig(opts?: RequestOptions): AxiosRequestConfig {
		const cfg: AxiosRequestConfig = {};
		if (opts?.headers) cfg.headers = { ...(cfg.headers ?? {}), ...opts.headers };
		if (opts?.params) cfg.params = opts.params;
		if (opts?.signal) cfg.signal = opts.signal; // Axios supports AbortController
		if (opts?.timeout !== undefined) cfg.timeout = opts.timeout;
		return cfg;
	}

	private normalizeError(error: unknown): ApiError {
		if (axios.isAxiosError(error)) {
			const axErr = error as AxiosError<unknown>;
			const payload = (axErr.response?.data as Record<string, unknown>) ?? {};
			return {
				message:
					(payload.message as string) ||
					axErr.message ||
					`HTTP Error ${axErr.response?.status ?? ''}`.trim(),
				status: axErr.response?.status,
				code: (payload.code as string) ?? axErr.code,
				details: (payload.details as unknown) ?? payload,
			};
		}
		return {
			message:
				(typeof error === 'object' && error && 'message' in error
					? String(error.message)
					: String(error)) || 'Unknown error',
		};
	}

	async get<T>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
		const res = await this.axios.get<ApiResponse<T>>(endpoint, this.buildConfig(options));
		return res.data;
	}

	async post<T>(
		endpoint: string,
		data?: unknown,
		options?: RequestOptions,
	): Promise<ApiResponse<T>> {
		const res = await this.axios.post<ApiResponse<T>>(
			endpoint,
			data ?? undefined,
			this.buildConfig(options),
		);
		return res.data;
	}

	async put<T>(
		endpoint: string,
		data?: unknown,
		options?: RequestOptions,
	): Promise<ApiResponse<T>> {
		const res = await this.axios.put<ApiResponse<T>>(
			endpoint,
			data ?? undefined,
			this.buildConfig(options),
		);
		return res.data;
	}

	async patch<T>(
		endpoint: string,
		data?: unknown,
		options?: RequestOptions,
	): Promise<ApiResponse<T>> {
		const res = await this.axios.patch<ApiResponse<T>>(
			endpoint,
			data ?? undefined,
			this.buildConfig(options),
		);
		return res.data;
	}

	async delete<T>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
		const res = await this.axios.delete<ApiResponse<T>>(endpoint, this.buildConfig(options));
		return res.data;
	}

	/**
	 * GET request that returns blob data for file downloads
	 * @param endpoint - API endpoint
	 * @param options - Optional request options
	 * @returns Promise resolving to the blob data
	 */
	async getBlob(endpoint: string, options?: RequestOptions): Promise<Blob> {
		const config = {
			...this.buildConfig(options),
			responseType: 'blob' as const,
		};
		const res = await this.axios.get(endpoint, config);
		return res.data;
	}

	/**
	 * POST request that returns blob data for file downloads
	 * @param endpoint - API endpoint
	 * @param data - Request body data
	 * @param options - Optional request options
	 * @returns Promise resolving to the blob data
	 */
	async postBlob(endpoint: string, data?: unknown, options?: RequestOptions): Promise<Blob> {
		const config = {
			...this.buildConfig(options),
			responseType: 'blob' as const,
		};
		const res = await this.axios.post(endpoint, data ?? undefined, config);
		return res.data;
	}

	/**
	 * Downloads a file directly to the user's device
	 * Handles blob URL creation and cleanup automatically
	 * @param endpoint - API endpoint to download from
	 * @param options - Optional request options
	 * @param contentType - MIME type of the file (defaults to 'application/pdf')
	 * @param filename - Optional filename for the download (will attempt to extract from response headers if not provided)
	 */
	async downloadFile(
		endpoint: string,
		options?: RequestOptions,
		contentType: string = 'application/pdf',
		filename?: string,
	): Promise<void> {
		const config = {
			...this.buildConfig(options),
			responseType: 'blob' as const,
		};
		const response = await this.axios.get(endpoint, config);
		const blob = response.data;

		// Try to extract filename from Content-Disposition header if not provided
		let downloadFilename = filename;
		if (!downloadFilename) {
			const contentDisposition = response.headers['content-disposition'];
			if (contentDisposition) {
				const filenameMatch = contentDisposition.match(
					/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
				);
				if (filenameMatch?.[1]) {
					downloadFilename = filenameMatch[1].replace(/['"]/g, '');
				}
			}
		}

		// Fallback to a default filename if still not set
		if (!downloadFilename) {
			const extension = contentType.split('/')[1] || 'pdf';
			downloadFilename = `document.${extension}`;
		}

		// Create a blob URL with the specified content type
		const typedBlob = new Blob([blob], { type: contentType });
		const blobUrl = window.URL.createObjectURL(typedBlob);

		// Create temporary anchor element and trigger download
		const link = document.createElement('a');
		link.href = blobUrl;
		link.download = downloadFilename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		// Clean up the blob URL after a short delay
		setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
	}

	// Method to add authentication token (applied by request interceptor)
	setAuthToken(token: string) {
		this.authToken = token;
	}

	// Method to remove authentication token
	removeAuthToken() {
		this.authToken = undefined;
	}

	// Method to set the token refresh callback
	setTokenRefreshCallback(callback: () => Promise<string | null>) {
		this.tokenRefreshCallback = callback;
	}

	// Method to remove the token refresh callback
	removeTokenRefreshCallback() {
		this.tokenRefreshCallback = undefined;
	}
}

// Create and export a default client instance
export const apiClient = new ApiClient();
